/**
 * The nest scene: background, nest, the clickable egg, the dinosaurs in the
 * yard, click particles, floating "+N" numbers, and the falling bonus egg.
 * Everything is drawn in native pixels with fillRect so it obeys the
 * pixel-art rules: no anti-aliased text, no fractional positions on screen.
 *
 * Animation state advances on logic ticks (20/s) and render() draws whatever
 * the current state is, so everything runs at the same speed on every display.
 */

import type { PixelCanvas } from './pixelCanvas';
import { CLICK_EGGS_MAX, clickEggCount, formatShort } from '../game/numbers';
import { createFloatLayer } from './floats';
import { yardSizeFor } from './yardGrowth';
import { driftShift } from '../game/parallax';
import { backgroundScrolls, waterMoves } from './motion';
import type { BackgroundName } from './sprites';
import { eraModifier } from '../game/rebirth';
import { DINOS, artSizeOf } from '../data/dinos';
import { FALL_SECONDS } from '../data/bonuses';
import { TICKS_PER_SECOND } from '../game/loop';
import {
  BACKGROUND_LAYERS,
  backgroundGroundColor,
  backgroundImage,
  backgroundLayers,
  backgroundSkyColor,
  SPECKLED_SKIES,
  outlineOf,
  silhouetteOf,
  inkBoundsOf,
  waterImage,
  sizedDinoSprite,
  sizedWalkSheet,
  meteorSprite,
  sceneProp,
  LOOPING_FLAPS,
} from './sprites';
import {
  METEOR_EMBER_COLORS,
  METEOR_FRAGMENT_COLORS,
  SHOWER_STAGGER_TICKS,
  clickMeteor,
  createYardMeteor,
  drawYardMeteor,
  fireballPosition,
  isOnMeteor,
  tickYardMeteor,
} from './meteor';
import type { MeteorSceneHooks, YardMeteor } from './meteor';
import type { BackgroundLayer, SpriteImage } from './sprites';
import type { Meteor } from '../game/meteors';
import { spriteIdFor } from '../game/inventory';
import { createInitialState } from '../game/state';
import { drawGoldenGlow, makeGlints, rimPoints } from './goldenGlow';
import type { RimPoint } from './goldenGlow';
import type { GameState } from '../game/state';

// ---------------------------------------------------------------------------
// Palette (Endesga 32)

const SKY = '#3a4466';
const GROUND = '#5a6988';
const NEST = '#b86f50';
const SHELL = '#f4f4f4';
const SHELL_SHADE = '#c0cbdc';
const SPOT = '#a7f070';
const GOLD = '#feae34';
/**
 * The stream: a ripple on the surface, the mud of its bank, and a stand-in
 * before the art loads. The ripple (a swimmer's waterline and wake) is a
 * muted blue-grey that reads on every era's water, light or dark; the old
 * bright cyan clashed with all of them (docs/mockups/ripple.html).
 */
const WATER_RIPPLE = '#8b9bb4';
/** The foam flecks at either end of a swimmer's waterline: a step lighter than the ripple, not white. */
const WATER_FOAM = '#c0cbdc';
/**
 * Eras whose water strip is a row of animation frames rather than one still
 * strip: the frame width and how long each frame shows. PixelLab animates at
 * most 256 px wide, so the frames are a 256 px slice of the era's water,
 * tiled with every other copy mirrored (as the paintings are) so each join is
 * seamless in every frame, and played forward then back, water washing up
 * the shore and drawing back (docs/mockups/giant-wave.html). Every era's is;
 * Settings can hold them all on their first frame (ui/motion.ts).
 */
const WAVES = { frame: 256, frameMs: 240 };
const ANIMATED_WATER: Partial<Record<BackgroundName, { frame: number; frameMs: number }>> = {
  default: WAVES,
  jurassic: WAVES,
  cretaceous: WAVES,
  'ice-age': WAVES,
  volcanic: WAVES,
  night: WAVES,
  giant: WAVES,
};
const WATER_DEEP = '#124e89';
const BANK_WET = '#733e39';
const BANK_EDGE = '#3e2731';
const GOLD_LIGHT = '#fee761';
const OUTLINE_WARM = '#3e2731';
const OUTLINE_COOL = '#181425';
const WHITE = '#ffffff';
const ROCK_DUST = '#8b9bb4';
const SPLICE = '#b55088';
const SPLICE_DEEP = '#68386c';
/** The shard shown after a broken meteor's "+N". */
const SHARD_ICON = `${import.meta.env.BASE_URL}art/icons/shard.png`;

/**
 * The evolution: sparks spiral in onto the dinosaur, a white flash, and the
 * new form is drawn from the next frame. Ticks, not seconds, at 20 a second.
 */
const EVOLVE_GATHER_TICKS = 26;
const EVOLVE_FLASH_TICKS = 3;
const EVOLVE_SETTLE_TICKS = 11;
const EVOLVE_TOTAL_TICKS = EVOLVE_GATHER_TICKS + EVOLVE_FLASH_TICKS + EVOLVE_SETTLE_TICKS;

/**
 * The Extinction: ten seconds over the yard at 20 ticks a second. The sky
 * goes out, a fireball crosses it, the impact whites out the screen — which
 * is where the era is swapped, so the herd is gone when the light clears —
 * and then ash falls on an empty yard while fossils surface in the dirt.
 */
const EX_DARKEN_TICKS = 50;
const EX_FALL_TICKS = 60;
const EX_FLASH_TICKS = 8;
const EX_ASH_TICKS = 82;
const EX_TOTAL_TICKS = EX_DARKEN_TICKS + EX_FALL_TICKS + EX_FLASH_TICKS + EX_ASH_TICKS;
/** Ordered darkest-first; the dither walks along it as the sky goes out. */
const ASH = '#8b9bb4';
const BONE = '#ead4aa';
const BONE_SHADE = '#c28569';

// ---------------------------------------------------------------------------
// Geometry. The drawing and the hit test read from the same numbers.

/**
 * Rows from the bottom of the canvas; layout() turns these into absolute
 * rows. The ground is the painting's own dirt band plus EXTRA_GROUND rows of
 * flat dirt below it, so the herd has depth to roam in front of the horizon.
 */
const EXTRA_GROUND = 60;
const GROUND_ROWS = 40 + EXTRA_GROUND;
let groundY = 0;
/**
 * The stream along the bottom of the yard (docs/water-plan.md, D3): the
 * bottom WATER_ROWS rows are water (or as many as the era's strip is tall:
 * the 48-row PixelLab strips rise to the nest), with a muddy lip
 * BANK_ROWS deep that wavers along its far edge. Swimmers ride its waterline, SWIM_LINE rows
 * down; nothing else lands in it.
 */
const WATER_ROWS = 24;
/** Rows a meteor keeps clear of the stream's top edge, so its bank lip doesn't cut the rock. */
const METEOR_BANK_CLEARANCE = 2;
const BANK_ROWS = 3;
const SWIM_LINE = 12;
/** How far down a swimmer's body the waterline cuts, as a share of its height. */
const SWIM_SINK = 0.5;
let waterTop = 0;
/**
 * The nest sits on the bank above the stream, in front of the herd, lifted
 * a few rows clear of the taller 48-row strips.
 * The sprite is 96 x 48 with its twigs on rows 6-41; NEST_Y is the row its
 * bottom twig lands on. The procedural fallback shares the same footprint.
 */
const NEST_ROWS_UP = WATER_ROWS + BANK_ROWS + 6;
let nestY = 0;
const NEST_SPRITE_W = 96;
const NEST_SPRITE_BOTTOM = 41;
const NEST_ROWS = 16;
const NEST_HALF_W = 48;

/**
 * The egg's centre column. The canvas width follows the viewport, so this is
 * set by layout() rather than fixed; everything horizontal hangs off it.
 */
let eggCx = 0;
/**
 * The egg sprite is 64 x 64 with the shell on columns 8-55 and rows 4-59;
 * these describe that shell for the hit test and the procedural fallback.
 */
const EGG_SPRITE = 64;
const EGG_SPRITE_BOTTOM = 59;
const EGG_TOP = -28;
const EGG_BOTTOM = 28;
const EGG_HALF_W = 24;
/** Center row, placed so the egg's bottom sits 13 rows into the nest. */
let eggCy = 0;

/** Half-width of an egg-shaped ellipse `dy` rows from its center. */
function eggHalfWidthAt(dy: number, top: number, bottom: number, halfW: number): number {
  const t = dy < 0 ? dy / top : dy / bottom;
  return Math.round(Math.sqrt(Math.max(0, 1 - t * t)) * halfW);
}

/** True if a native-pixel coordinate lands on the (unsquashed) nest egg. */
function isOnEgg(x: number, y: number): boolean {
  const dy = y - eggCy;
  if (dy < EGG_TOP || dy > EGG_BOTTOM) return false;
  return Math.abs(x - eggCx) <= eggHalfWidthAt(dy, EGG_TOP, EGG_BOTTOM, EGG_HALF_W);
}

// ---------------------------------------------------------------------------
// Squash. A click flattens the egg for a few ticks; the bottom stays put in
// the nest and the top comes down while the sides bulge.

const SQUASH_TICKS = 4;
/**
 * The hatch strip: 96px frames whose 64px egg window sits at 16,20. Frames
 * 2-7 are the shell splitting open and falling apart (the hatchling
 * silhouette the render put inside was cut out, so the shell is hollow);
 * they play when a new species is hatched, the last one holding a moment.
 * Frames 0-1 are cracked shells, unused: clicks squash the egg instead.
 */
const EGG_STRIP_FRAME = 96;
const EGG_STRIP_INSET_X = 16;
const EGG_STRIP_INSET_Y = 20;
const EGG_STRIP_FRAMES = 8;
const HATCH_FIRST_FRAME = 2;
const HATCH_FRAME_TICKS = 3;
/** The last hatch frame holds this long before the egg is whole again. */
const HATCH_HOLD_TICKS = 16;
/** [rows shaved off the top, columns added to each side], indexed by ticks left. */
const SQUASH_FRAMES: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  [2, 1],
  [4, 2],
  [6, 4],
  [6, 4],
];

// ---------------------------------------------------------------------------
// Particles: egg fragments and sparkles. Positions are floats internally and
// rounded at draw time, so motion is smooth but every pixel lands on the grid.

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Side of the square a plain particle is drawn as; ignored by 'egg'. */
  size: number;
  color: string;
  ticksLeft: number;
  gravity: number;
  /** Square by default. An 'egg' is stamped from EGG_PARTICLE_SPANS instead. */
  shape?: 'egg';
}

const MAX_PARTICLES = 200;
const FRAGMENT_GRAVITY = 0.25;

/**
 * The little egg a click throws off: four wide, five tall, as one span per
 * row from its top-left. Spans rather than a pixel loop because two hundred
 * particles at sixteen pixels each is a lot of fillRect for one frame.
 *
 * Narrower at the top and bottom than through the middle, which is what makes
 * it read as an egg at this size rather than as a blob.
 */
const EGG_PARTICLE_SPANS: readonly (readonly [number, number])[] = [
  [1, 2],
  [0, 4],
  [0, 4],
  [0, 4],
  [1, 2],
];
const EGG_PARTICLE_W = 4;
const EGG_PARTICLE_H = EGG_PARTICLE_SPANS.length;

/**
 * How hard a burst throws, from a single egg to a full eight. It multiplies
 * the speed and the upward kick together, so a big click reads as bigger
 * across and higher rather than just as more eggs in the same little puff.
 */
const EGG_SPREAD_MIN = 0.6;
const EGG_SPREAD_MAX = 1.6;

// ---------------------------------------------------------------------------
// The yard: one dinosaur per hatched tier, drawn bigger as its level rises.
// Each tier keeps its own depth row (or altitude, for flyers) and wanders the
// whole width of it: walk to a spot, rest, pick another. The nest and egg are
// drawn after the herd, so a dinosaur crossing the middle passes behind them.

/** Phase spread for the herd's animations, so they don't move in unison. */
const BOB_PERIOD_TICKS = TICKS_PER_SECOND / 2;
/** Walking bob is quicker, so a moving dinosaur waddles. */
const WALK_BOB_TICKS = 4;
/**
 * Ticks per frame of a real animation strip. Walk strips are trimmed to one
 * full gait cycle and loop forward (a 12-frame cycle at 2 ticks a frame is
 * 1.2 s). Flyer strips are a single wingbeat stroke, which is symmetric, so
 * they play ping-pong: 0..n-1 then back to 1, except those in LOOPING_FLAPS.
 */
const WALK_FRAME_TICKS = 2;
/**
 * Speeds in native pixels per tick (20 ticks/s). Each dinosaur has its own
 * pace, seeded per tier within these bounds, and every trip varies it a
 * little more, so the herd doesn't march in lockstep. Flyers are quicker.
 */
const WALK_SPEED_MIN = 0.35;
const WALK_SPEED_MAX = 0.65;
const FLY_SPEED_MIN = 0.6;
const FLY_SPEED_MAX = 1.1;
/** Per-trip variation around the dinosaur's pace, as a fraction (±). */
const TRIP_SPEED_JITTER = 0.25;
/** The pace the walk strips' cadence (WALK_FRAME_TICKS) was tuned for. */
const USUAL_WALK_SPEED = (WALK_SPEED_MIN + WALK_SPEED_MAX) / 2;
/**
 * A newly hatched dinosaur steps out of the egg as the shell falls apart,
 * HATCH_EMERGE_TICKS into the hatch strip, and dashes straight out to a far
 * side of the yard on its own track at HATCH_DASH_SPEED, several times the
 * herd's walking pace, catching its breath there before it wanders like the
 * rest.
 */
const HATCH_EMERGE_TICKS = 6;
const HATCH_DASH_SPEED = 2;
const HATCH_ARRIVE_REST_TICKS = TICKS_PER_SECOND;
/** Where that dash ends: this far in from one edge of its track (0-1), either side. */
const HATCH_EDGE_MIN = 0.02;
const HATCH_EDGE_MAX = 0.08;
/**
 * Walkers run from where a meteor is about to land. The spot is picked as
 * the fall starts, a second before impact, so a dinosaur within
 * SCATTER_RADIUS of it (centre to centre, across the yard) bolts
 * SCATTER_REACT_TICKS after the fireball appears, at SCATTER_SPEED, to
 * SCATTER_CLEAR past the spot on its own side, and stands there a while.
 * At each rock's thud every walker in the yard jumps STARTLE_PIXELS, near
 * or not. Chosen from three versions (docs/mockups/meteor-scatter.html, B);
 * the one not taken sent the nearest runner back to sniff the rock.
 */
const SCATTER_RADIUS = 56;
const SCATTER_CLEAR = 76;
const SCATTER_REACT_TICKS = 3;
const SCATTER_SPEED = 2.2;
const SCATTER_REST_TICKS = TICKS_PER_SECOND;
/**
 * A walker between the rock and the yard's edge runs as far as it can; if
 * that leaves it within SCATTER_CORNERED of the spot it cowers there,
 * shivering a pixel, for SCATTER_COWER_TICKS.
 */
const SCATTER_CORNERED = 40;
const SCATTER_COWER_TICKS = 3 * TICKS_PER_SECOND;
const SHIVER_TICKS = 2;
const STARTLE_TICKS = 6;
const STARTLE_PIXELS = 4;
/**
 * A device asking for reduced motion keeps the running (it is only walking,
 * faster) but not the hop or the shiver, which are motion for its own sake.
 */
const REDUCED_MOTION =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
/** How long a dinosaur stands before wandering again, in ticks. */
const REST_MIN_TICKS = TICKS_PER_SECOND;
const REST_MAX_TICKS = 4 * TICKS_PER_SECOND;
/**
 * Walkers keep to three depth rows, ROW_STEP apart, but now and then a trip
 * drifts onto the next row over, easing across as it walks. Only on a trip
 * long enough to make the slope gentle; a short one would read as a slide.
 */
const WALK_ROWS = 3;
const ROW_STEP = 20;
const ROW_SWITCH_CHANCE = 0.3;
const ROW_SWITCH_MIN_TRIP = 0.3;
/** Flyers: the row their sprite's bottom edge hovers on, and the wingbeat. */
/**
 * Flyers hover in bands between SKY_LOW_ABOVE_GROUND above the ground row
 * and SKY_TOP_MARGIN below the canvas top, spread evenly over whatever sky
 * the canvas has (at least SKY_MIN_STEP apart).
 */
const SKY_LOW_ABOVE_GROUND = 82;
const SKY_TOP_MARGIN = 64;
const SKY_MIN_STEP = 24;
const SKY_BANDS = 2;
const FLAP_TICKS = 6;
const FLAP_PIXELS = 2;

interface YardSlot {
  /** In the air (flyers) rather than on the ground. */
  sky: boolean;
  /** In the stream along the bottom (swimmers). */
  water: boolean;
  /**
   * Where along its side the sprite's left edge sits, 0-1 of the room left
   * over after the sprite's own width, so it stays in bounds at every size.
   */
  along: number;
  /** Where it is walking to, in the same units; equal to `along` when resting. */
  target: number;
  /** Ticks left standing still before choosing a new target. */
  rest: number;
  /** Sprites are drawn facing left; this mirrors one that last walked right. */
  facingRight: boolean;
  /** Pixels of travel that 0-1 spans, as of the last draw; converts speed to steps. */
  travel: number;
  /** This dinosaur's usual pace, pixels per tick. */
  pace: number;
  /** The tier's pace multiplier (DinoDef.pace); a runner crosses the yard instead of ambling. */
  paceMult: number;
  /**
   * Animation phase in ticks. Walkers advance it in step with their ground
   * speed so the feet grip; flyers advance it every tick (a fixed wingbeat).
   */
  phase: number;
  /** The pace on the current trip: `pace` jittered when the target was chosen. */
  speed: number;
  /** Depth row (walkers) or altitude band (flyers); footY() resolves it. */
  row: number;
  /** A walker's row at the start of this trip, and the one it ends on. */
  rowFrom: number;
  rowTo: number;
  /** Where this trip started, to tell how far across the row change it is. */
  tripFrom: number;
  /** Phase offset so the herd doesn't bob in unison. */
  bobOffset: number;
  /** Where the sprite's window landed on the last draw, for the click hit test. */
  box: { x: number; y: number; w: number; h: number } | null;
  /**
   * Just hatched: ticks until it steps out of the egg (hidden till then), how
   * far along its dash to its track it is (0-1), and that dash's length in
   * native pixels as of the last draw. Null once it has arrived.
   */
  hatching: { wait: number; t: number; dist: number } | null;
  /**
   * Running from a meteor: ticks until it bolts (the rock's own place in its
   * shower, plus a beat to notice), and where it runs to. Null otherwise.
   */
  scatter: { wait: number; to: number; cornered: boolean } | null;
  /** Ticks left of a startled hop. */
  hop: number;
  /** Ran into the yard's edge still near the rock: shivers while it waits. */
  cowering: boolean;
  /**
   * Having run from a meteor, the stretch of its track it keeps to (0-1, as
   * `along`) until the rock on each side of it has cooled or been broken:
   * `lo` while `left` is still there, `hi` while `right` is.
   */
  keepOut: {
    left: YardMeteor | null;
    lo: number;
    right: YardMeteor | null;
    hi: number;
  } | null;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const YARD_MARGIN = 4;

/** One slot per tier, index-aligned with DINOS. Starting spots are seeded. */
const YARD: readonly YardSlot[] = (() => {
  const rand = mulberry32(0xd1_00);
  let flyers = 0;
  return DINOS.map((def) => {
    const along = rand();
    const sky = def.flies === true;
    const water = def.swims === true;
    // Flyers take turns at different altitudes; walkers take a depth row.
    const row = sky ? flyers++ % SKY_BANDS : Math.floor((def.tier - 1) / 2) % WALK_ROWS;
    return {
      sky,
      water,
      along,
      target: along,
      rest: Math.floor(rand() * REST_MAX_TICKS),
      pace:
        (sky
          ? FLY_SPEED_MIN + rand() * (FLY_SPEED_MAX - FLY_SPEED_MIN)
          : WALK_SPEED_MIN + rand() * (WALK_SPEED_MAX - WALK_SPEED_MIN)) * (def.pace ?? 1),
      paceMult: def.pace ?? 1,
      phase: 0,
      speed: 0,
      facingRight: false,
      travel: 100,
      row,
      rowFrom: row,
      rowTo: row,
      tripFrom: along,
      bobOffset: Math.floor(rand() * BOB_PERIOD_TICKS * 2),
      box: null,
      hatching: null,
      scatter: null,
      hop: 0,
      cowering: false,
      keepOut: null,
    };
  });
})();

/** The row a slot's feet stand on (or a flyer's sprite bottom hovers at). */
function footY(slot: YardSlot): number {
  // A swimmer's "feet" are the waterline it is cut at.
  if (slot.water) return waterTop + SWIM_LINE;
  if (!slot.sky) return groundY + 10 + Math.round(walkRow(slot) * ROW_STEP);
  const low = groundY - SKY_LOW_ABOVE_GROUND;
  const step = Math.max(SKY_MIN_STEP, Math.floor((low - SKY_TOP_MARGIN) / SKY_BANDS));
  return low - slot.row * step;
}

/** A walker's depth row right now: between two rows while it changes, eased at both ends. */
function walkRow(slot: YardSlot): number {
  if (slot.rowFrom === slot.rowTo) return slot.rowTo;
  const span = slot.target - slot.tripFrom;
  const t = span === 0 ? 1 : Math.min(1, Math.max(0, (slot.along - slot.tripFrom) / span));
  const eased = t * t * (3 - 2 * t);
  return slot.rowFrom + (slot.rowTo - slot.rowFrom) * eased;
}

/** Advance one dinosaur's wander by a tick. Visual only; uses Math.random. */
function tickRoam(slot: YardSlot, frozen = false): void {
  if (frozen) {
    // Stand still through the transformation, where it is (even between rows).
    const row = walkRow(slot);
    slot.rowFrom = slot.rowTo = slot.row = row;
    slot.target = slot.along;
    return;
  }
  const hatching = slot.hatching;
  if (hatching) {
    if (hatching.wait > 0) {
      hatching.wait--;
      return;
    }
    hatching.t = Math.min(1, hatching.t + HATCH_DASH_SPEED / Math.max(1, hatching.dist));
    slot.phase += slot.sky ? 1 : HATCH_DASH_SPEED / USUAL_WALK_SPEED;
    if (hatching.t >= 1) {
      slot.hatching = null;
      slot.target = slot.along;
      slot.rest = HATCH_ARRIVE_REST_TICKS;
    }
    return;
  }
  if (slot.hop > 0) slot.hop--;
  const scatter = slot.scatter;
  if (scatter) {
    if (scatter.wait > 0) {
      scatter.wait--;
    } else {
      // Bolt: flat out along its own row, whatever it was doing.
      slot.scatter = null;
      slot.row = slot.rowFrom = slot.rowTo = Math.round(walkRow(slot));
      slot.tripFrom = slot.along;
      slot.target = scatter.to;
      slot.facingRight = slot.target > slot.along;
      slot.speed = SCATTER_SPEED;
      slot.rest = SCATTER_REST_TICKS;
      // Cornered, it waits longer: until the rock is down and then some.
      slot.cowering = scatter.cornered;
      if (scatter.cornered) slot.rest = SCATTER_COWER_TICKS;
    }
  }
  // Wings beat whether or not the flyer is going anywhere.
  if (slot.sky) slot.phase += 1;
  if (slot.along === slot.target) {
    // Arrived: the row change, if any, is done.
    slot.rowFrom = slot.rowTo;
    if (--slot.rest > 0) return;
    slot.cowering = false;
    // A runner (pace above 1) crosses the yard rather than ambling a few
    // steps: it heads for the far side and barely stops before turning.
    const runner = slot.paceMult > 1;
    // Keeping away from a rock that is still there: the trip is chosen as
    // usual, then squeezed into the stretch it keeps to.
    let lo = 0;
    let hi = 1;
    const keep = slot.keepOut;
    if (keep) {
      if (keep.left && keep.left.phase !== 'ending') lo = keep.lo;
      if (keep.right && keep.right.phase !== 'ending') hi = keep.hi;
      if (lo === 0 && hi === 1) slot.keepOut = null;
    }
    const inRange = (lo + hi) / 2;
    const pick = runner
      ? slot.along < inRange
        ? 0.7 + Math.random() * 0.3
        : Math.random() * 0.3
      : Math.random();
    slot.target = lo + pick * (hi - lo);
    slot.facingRight = slot.target > slot.along;
    // Now and then a walker's trip ends on the next row over.
    // (From where it stands, which is between rows only after a freeze.)
    slot.row = Math.round(slot.rowFrom);
    slot.rowTo = slot.row;
    slot.tripFrom = slot.along;
    if (
      !slot.sky &&
      !slot.water &&
      Math.abs(slot.target - slot.along) >= ROW_SWITCH_MIN_TRIP &&
      Math.random() < ROW_SWITCH_CHANCE
    ) {
      const up = slot.row === WALK_ROWS - 1 || (slot.row > 0 && Math.random() < 0.5);
      slot.rowTo = slot.row + (up ? -1 : 1);
    }
    slot.speed = slot.pace * (1 + (Math.random() * 2 - 1) * TRIP_SPEED_JITTER);
    // Flyers barely pause between glides; walkers stand around a while.
    const [min, max] = slot.sky || runner ? [0, REST_MIN_TICKS] : [REST_MIN_TICKS, REST_MAX_TICKS];
    slot.rest = min + Math.floor(Math.random() * (max - min));
    return;
  }
  const speed = slot.speed || slot.pace;
  const step = speed / Math.max(1, slot.travel);
  if (Math.abs(slot.target - slot.along) <= step) slot.along = slot.target;
  else slot.along += slot.target > slot.along ? step : -step;
  // The strip's stride was tuned to the herd's usual pace at WALK_FRAME_TICKS
  // per frame; a faster trip steps proportionally more often.
  if (!slot.sky) slot.phase += speed / USUAL_WALK_SPEED;
}

let laidOutWidth = -1;
let laidOutHeight = -1;

/** Positions everything for a canvas `width` x `height` pixels. */
function layout(width: number, height: number): void {
  if (width === laidOutWidth && height === laidOutHeight) return;
  laidOutWidth = width;
  laidOutHeight = height;
  eggCx = Math.floor(width / 2);
  groundY = height - GROUND_ROWS;
  waterTop = height - WATER_ROWS;
  nestY = height - NEST_ROWS_UP;
  eggCy = nestY - 13 - EGG_BOTTOM;
}

/** Horizontal room a slot can roam, and where it starts: the whole width, inside a margin. */
function yardRoom(width: number): { start: number; room: number } {
  return { start: YARD_MARGIN, room: width - 2 * YARD_MARGIN };
}

/** Placeholder size grows with tier so later dinosaurs read as bigger. */
function yardSpriteSize(tier: number): number {
  return 8 + Math.floor((tier - 1) / 3) * 2;
}

// ---------------------------------------------------------------------------
// The falling egg.

const FALL_TICKS = FALL_SECONDS * TICKS_PER_SECOND;
/** The last part of its time on screen is spent rolling off the edge. */
const ROLL_TICKS = 2 * TICKS_PER_SECOND;
const FALLING_SIZE = 24;
const FALLING_HALF = FALLING_SIZE / 2;
/** Extra pixels around the sprite that still count as a catch. */
const CATCH_SLOP = 4;
/** Where a falling egg lands, relative to the ground row. */
const FALLING_LAND_ABOVE_GROUND = FALLING_HALF - 6;
const FALLING_START_Y = -FALLING_HALF;
const ROLL_SPEED = 4;

/**
 * An egg jackpot's reveal (ui/floats.ts draws the figure): a gold sunburst
 * behind it, eggs thrown from the shell, and sparkles drifting up round it.
 * Its length matches the figure's CSS animation, 2.6 s.
 */
const JACKPOT_MS = 2600;
/** The sunburst opens over this long, and fades from JACKPOT_FADE_MS to the end. */
const JACKPOT_OPEN_MS = 300;
const JACKPOT_FADE_MS = 2000;
const JACKPOT_RAYS = 12;
/** A ray's width, as a share of the gap between rays. */
const JACKPOT_RAY_SHARE = 0.38;
const JACKPOT_RAY_RADIUS = 56;
/** Radians a second; held still for reduced motion. */
const JACKPOT_SPIN = 0.5;
const JACKPOT_RAY_ALPHA = 0.45;
const JACKPOT_EGGS = 26;
/** Wider than a click's widest throw (EGG_SPREAD_MAX). */
const JACKPOT_EGG_SPREAD = 2;
const JACKPOT_GOLD_EGGS = 0.35;
/** Sparkles every few ticks for this long, over an area round the figure. */
const JACKPOT_SPARKLE_TICKS = 32;
const JACKPOT_SPARKLE_W = 90;
const JACKPOT_SPARKLE_H = 30;

interface FallingEgg {
  x: number;
  y: number;
  ticksLeft: number;
  /** Which way it rolls once it lands. */
  dir: 1 | -1;
  age: number;
}

// ---------------------------------------------------------------------------

export interface NestScene {
  dinos: Readonly<Record<string, number>>;
  /** The whole state, so the yard can draw evolved sprites. */
  state: GameState;
}

/** What an egg jackpot shows over its catch: "+742 million", and a line under it. */
export interface JackpotReveal {
  amount: string;
  caption: string;
}

export interface NestHooks {
  /** The nest egg was hit; returns the eggs granted, shown as a floating number. */
  onEggClick(): number;
  /** A falling egg was caught; an egg jackpot returns what to show for it. */
  onFallingEggCaught(): JackpotReveal | null;
  /** A dinosaur in the yard was clicked. */
  onDinoClick(id: string): void;
  /** Damage a click does to a meteor (the player's tool). */
  meteorDamage(): number;
  /** A fall has begun: the rocks are on their way down (the rush of air). */
  onMeteorIncoming(): void;
  /** A meteor landed (thud). */
  onMeteorImpact(): void;
  /** A click chipped a meteor (clink). */
  onMeteorHit(): void;
  /** A meteor broke; returns the shards paid, shown as a floating number. */
  onMeteorBroken(meteor: Meteor): number;
  /** Every rock of a shower (two or more) broken, none left to cool. */
  onShowerCleared(): void;
}

/** The burst's length (0.6 s), which the eggs counter's count-up matches. */
export const COLLECT_FOUNTAIN_TICKS = 12;
/** Eggs thrown in the burst's first instant, and how fast they fly. */
const COLLECT_BURST_EGGS = 32;
const COLLECT_BURST_SPEED_MIN = 3.5;
const COLLECT_BURST_SPEED_SPREAD = 3.5;

export interface NestView {
  /** Advance every animation by one logic tick. */
  tick(): void;
  /** Redraw the whole scene. */
  render(scene: NestScene): void;
  /** Drop a bonus egg from the top of the screen. No-op if one is already falling. */
  spawnFallingEgg(): void;
  /**
   * Play the egg hatching (a new species was hatched); `dinoId` steps out of
   * it and dashes to its track.
   */
  hatch(dinoId?: string): void;
  /**
   * Eggs arriving in a haul (the welcome-back panel's Collect): the egg
   * squashes once, as for a click, and the eggs burst out of it, a wide
   * spray all at once with a sparkle pop, then a short tapering spray to the
   * end of COLLECT_FOUNTAIN_TICKS, while the caller rolls the eggs counter up
   * to match; then sparkles and the "+N" total rise from it. Chosen from three
   * (docs/mockups/welcome-collect.html, B) over a volley of clicks and each
   * dinosaur popping its own share; a burst rather than a slow fountain since
   * 2026-09-30.
   */
  collectEggs(eggs: number): void;
  /**
   * Play a dinosaur evolving: sparks gather on it, a flash, and it stands up
   * as its new form. The caller has already changed the state, so the new
   * sprite is what emerges.
   */
  playEvolution(dinoId: string): void;
  /**
   * Play the Extinction over the yard. `onBuried` fires inside the impact
   * flash, when the screen is white and the era can be swapped unseen.
   */
  playExtinction(onBuried: () => void): void;
  /** True while it is playing, so the rest of the game can hold still. */
  readonly extincting: boolean;
  /**
   * Whether the era's painting and water, the nest and the egg have all
   * loaded, so the yard is drawn as itself rather than its stand-in. The
   * loading screen waits for it (ui/loader.ts).
   */
  readonly artReady: boolean;
  /** Drop a meteor into the yard. No-op if one is already there. */
  /** One fall: a single rock, or a whole shower of the small kinds. */
  spawnMeteor(fall: readonly Meteor[]): void;
  readonly hasMeteor: boolean;
  /**
   * Parallax: a horizontal shift in native pixels per layer (positive moves
   * the layer right). Layers not named stay put. Zero everywhere draws the
   * painting exactly as it was.
   */
  setParallax(offsets: Partial<Record<BackgroundLayer, number>>): void;
  readonly hasFallingEgg: boolean;
  /**
   * The dinosaurs a held item could be used on, or null when nothing is held.
   * They are outlined in the evolution violet and twinkle, so the yard says
   * who can take it as the list does.
   */
  setEvolveTargets(ids: ReadonlySet<string> | null): void;
}

export function createNestView(pc: PixelCanvas, hooks: NestHooks): NestView {
  const { canvas, ctx } = pc;
  const floatLayer = createFloatLayer(pc);
  /**
   * Seconds of the background's scroll and of the water's waves, kept on the
   * wall clock rather than counted in ticks.
   *
   * A tick is 50ms, and a layer at 2.2 px/s wants to step every 454, which
   * falls between nine ticks and ten — so the steps came 450, 450, 500, 450,
   * and the unevenness read as a stutter on top of the stepping itself.
   *
   * Real frame deltas instead, accumulated here rather than read from the
   * clock directly: a hidden tab stops rendering, and the drift should pause
   * with it rather than leap forward on return. A gap longer than a few
   * frames is treated as one of those pauses and skipped.
   *
   * Each advances only while Settings has it on (ui/motion.ts): turned off,
   * the background holds where it is rather than jumping home, and the water
   * goes back to its first frame.
   */
  let scrollSeconds = 0;
  let waveSeconds = 0;
  let driftClock = 0;
  let squashTicksLeft = 0;
  /** Things to do a number of ticks from now (a volley's clicks, a toss). */
  let timers: { at: number; run: () => void }[] = [];
  const after = (ticks: number, run: () => void): void => {
    timers.push({ at: tickCount + ticks, run });
  };
  /** Ticks into the hatch strip, or -1 when not hatching. */
  let hatchTick = -1;
  let tickCount = 0;
  const particles: Particle[] = [];
  let falling: FallingEgg | null = null;
  /** The jackpot reveal playing: its centre, when it began, and its age in ticks. */
  let jackpot: { x: number; y: number; start: number; age: number } | null = null;
  /** The sunburst's pixels, made once on first need. */
  let rays: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; image: ImageData } | null =
    null;
  /** Every rock in the yard: one, or a whole shower of the small kinds. */
  let yardMeteors: YardMeteor[] = [];
  /** The fall in the yard: how many rocks, how many broken, whether one cooled. */
  let shower = { size: 0, broken: 0, cooled: false };
  /** The last state handed to render(); the evolved sprite lookup reads it. */
  let sceneState: GameState = createInitialState(0);
  /** The tier index mid-evolution and how far in, or null. */
  let evolving: { tier: number; age: number } | null = null;
  /** Dinosaurs a held item can be used on (setEvolveTargets), or null. */
  let evolveTargets: ReadonlySet<string> | null = null;
  /** The extinction sequence, if one is playing. */
  let extincting: { age: number; onBuried: () => void; buried: boolean } | null = null;
  /** Where the fossils surface, picked once when the herd goes under. */
  let bones: { x: number; y: number; kind: number }[] = [];
  const parallax: Record<BackgroundLayer, number> = {
    sky: 0,
    far: 0.4,
    near: 1,
    ground: 0,
    trees: 2.2,
  };
  /** Whether the background is drawn as drifting layers or as the painting. */
  const PARALLAX: boolean = false;
  /**
   * The painting scrolls slowly left as one image, dirt and all, looping
   * through its mirrored copies. Native px a second: at 1 a 768-wide painting
   * takes about 25 minutes to come round. Chosen over holding the dirt still.
   */
  const BACKGROUND_SCROLL = 1;

  /**
   * Ambient drift, native px per second per layer.
   *
   * Two layers move: the range, and the palms in front of it at more than
   * twice the speed. The eye reads the gap between them as depth, and two is
   * enough to sell it.
   *
   * The other three hold still, each for its own reason. The sky's stars
   * would streak. The ground carries the nest, and dirt sliding under a fixed
   * nest reads as the world moving rather than the camera. And far is nine
   * rows of peak tips with no body under them — the painting only ever showed
   * the parts of them that cleared the range — so drifting it would sooner or
   * later slide a tip over one of the range's valleys to hang on nothing.
   */
  const DRIFT_PX_PER_SECOND: Record<BackgroundLayer, number> = {
    sky: 0,
    far: 0,
    near: 1,
    ground: 0,
    trees: 2.2,
  };
  // Ask for the gold egg and the hatch strip now so their first use is not a placeholder.
  sceneProp('egg-gold');
  sceneProp('egg-hatch');

  // Visual-only randomness; nothing here affects the economy.
  const rand = Math.random;

  // ---- particles -----------------------------------------------------------

  function emit(p: Particle): void {
    if (particles.length >= MAX_PARTICLES) particles.shift();
    particles.push(p);
  }

  function burstFragments(x: number, y: number, colors: readonly string[], count: number): void {
    for (let i = 0; i < count; i++) {
      const angle = rand() * Math.PI - Math.PI; // upward half-circle
      const speed = 1.5 + rand() * 2;
      emit({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        size: 2 + Math.floor(rand() * 2),
        color: colors[Math.floor(rand() * colors.length)] ?? WHITE,
        ticksLeft: 14 + Math.floor(rand() * 8),
        gravity: FRAGMENT_GRAVITY,
      });
    }
  }

  /**
   * Eggs thrown off the shell when it is clicked: the same upward half-circle
   * the shell chips use, a little slower and living a little longer, so they
   * arc and fall rather than spraying. Drawn from their top-left, so each is
   * offset onto the point it came from. How many is `clickEggCount`, on what
   * the click was worth, and how far they go rises with it: one egg barely
   * leaves the shell, eight go wide and high.
   */
  function burstEggs(
    x: number,
    y: number,
    count: number,
    spread = EGG_SPREAD_MIN +
      ((EGG_SPREAD_MAX - EGG_SPREAD_MIN) * (Math.max(1, count) - 1)) / (CLICK_EGGS_MAX - 1),
    goldShare = 0,
  ): void {
    for (let i = 0; i < count; i++) {
      const angle = rand() * Math.PI - Math.PI;
      const speed = (1.4 + rand() * 1.8) * spread;
      emit({
        x: x - EGG_PARTICLE_W / 2,
        y: y - EGG_PARTICLE_H / 2,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.2 * spread,
        size: 1,
        // A few in the shaded tone, so a handful of them do not read as one
        // flat sheet of white; a jackpot's are partly gold.
        color: rand() < goldShare ? GOLD_LIGHT : rand() < 0.3 ? SHELL_SHADE : SHELL,
        ticksLeft: 20 + Math.floor(rand() * 10),
        gravity: FRAGMENT_GRAVITY,
        shape: 'egg',
      });
    }
  }

  function burstSparkles(x: number, y: number, count: number, w = 24, h = 16): void {
    for (let i = 0; i < count; i++) {
      emit({
        x: x + (rand() - 0.5) * w,
        y: y + (rand() - 0.5) * h,
        vx: 0,
        vy: -0.6 - rand() * 0.6,
        size: 2,
        color: rand() < 0.5 ? GOLD_LIGHT : WHITE,
        ticksLeft: 8 + Math.floor(rand() * 6),
        gravity: 0,
      });
    }
  }

  // ---- meteors -------------------------------------------------------------

  /**
   * Sends every walker near a landing spot running from it (see
   * SCATTER_RADIUS), each from the nearest rock of the fall and on its own
   * side of it.
   */
  function scatterFrom(rocks: readonly YardMeteor[]): void {
    const { start } = yardRoom(pc.width);
    YARD.forEach((slot, tier) => {
      const b = slot.box;
      if (!b || slot.sky || slot.water || slot.hatching || slot.travel <= 0) return;
      if (evolving?.tier === tier) return;
      const centre = b.x + b.w / 2;
      let rock: YardMeteor | null = null;
      let dist = Infinity;
      for (const r of rocks) {
        const d = Math.abs(r.x - centre);
        if (d < dist) {
          dist = d;
          rock = r;
        }
      }
      if (!rock || dist > SCATTER_RADIUS) return;
      const alongOf = (px: number): number => Math.min(1, Math.max(0, (px - start) / slot.travel));
      // It stays in the gap it is in, between the rocks either side of it
      // (or a rock and the yard's edge), and never crosses one: in a shower,
      // running from the nearer rock must not take it onto the next.
      let leftRock: YardMeteor | null = null;
      let rightRock: YardMeteor | null = null;
      for (const r of rocks) {
        if (r.x <= centre && (!leftRock || r.x > leftRock.x)) leftRock = r;
        if (r.x > centre && (!rightRock || r.x < rightRock.x)) rightRock = r;
      }
      const away = rock === leftRock ? 1 : -1;
      let aim = rock.x + away * SCATTER_CLEAR;
      const lowest = leftRock ? leftRock.x + SCATTER_RADIUS : -Infinity;
      const highest = rightRock ? rightRock.x - SCATTER_RADIUS : Infinity;
      // Too narrow a gap to be clear of both: the middle of it, cowering.
      const squeezed = lowest > highest;
      if (squeezed && leftRock && rightRock) aim = (leftRock.x + rightRock.x) / 2;
      else aim = Math.min(highest, Math.max(lowest, aim));
      const to = alongOf(aim - b.w / 2);
      const endCentre = start + to * slot.travel + b.w / 2;
      slot.scatter = {
        wait: rock.delay + SCATTER_REACT_TICKS,
        to,
        cornered: squeezed || Math.abs(endCentre - rock.x) < SCATTER_CORNERED,
      };
      // After that it keeps to that gap, SCATTER_RADIUS clear of each rock,
      // until each has cooled or been broken (a cornered one stays put).
      slot.keepOut = {
        left: leftRock,
        lo: leftRock ? Math.min(to, alongOf(leftRock.x + SCATTER_RADIUS - b.w / 2)) : 0,
        right: rightRock,
        hi: rightRock ? Math.max(to, alongOf(rightRock.x - SCATTER_RADIUS - b.w / 2)) : 1,
      };
    });
  }

  const meteorHooks: MeteorSceneHooks = {
    damage: () => hooks.meteorDamage(),
    onImpact(x, y) {
      // Dirt thrown up on landing.
      const ground = backgroundGroundColor(backgroundNow().name) ?? GROUND;
      burstFragments(x, y, [ground, OUTLINE_COOL, ROCK_DUST], 10);
      // The whole herd on the ground jumps at the thud.
      if (!REDUCED_MOTION?.matches) {
        for (const slot of YARD) {
          if (!slot.sky && !slot.water && !slot.hatching) slot.hop = STARTLE_TICKS;
        }
      }
      hooks.onMeteorImpact();
    },
    onHit(x, y) {
      burstFragments(x, y, METEOR_FRAGMENT_COLORS, 3);
      hooks.onMeteorHit();
    },
    onBroken(m, x, y) {
      burstFragments(x, y, METEOR_FRAGMENT_COLORS, 16);
      burstSparkles(x, y - 6, 6);
      const shards = hooks.onMeteorBroken(m.meteor);
      if (shards > 0) floatLayer.show(x, y - 8, `+${shards}`, SHARD_ICON);
      shower.broken++;
      if (shower.size > 1 && shower.broken === shower.size && !shower.cooled)
        hooks.onShowerCleared();
      return shards;
    },
    onCooled() {
      shower.cooled = true;
    },
  };

  /** The front-most rock under a native-pixel point, or null. */
  function meteorAt(x: number, y: number): YardMeteor | null {
    // Drawn back to front, so the last match is the one on top.
    for (let i = yardMeteors.length - 1; i >= 0; i--) {
      const m = yardMeteors[i]!;
      if (isOnMeteor(m, x, y)) return m;
    }
    return null;
  }

  // ---- input ---------------------------------------------------------------

  function isOnFallingEgg(x: number, y: number): boolean {
    if (!falling) return false;
    const r = FALLING_HALF + CATCH_SLOP;
    return Math.abs(x - falling.x) <= r && Math.abs(y - falling.y) <= r;
  }

  /** Tiers drawn last frame, back to front; the hit test walks it front to back. */
  let drawnTiers: number[] = [];

  /** The topmost dinosaur under a point, or -1. The egg is checked first by the caller. */
  function dinoAt(x: number, y: number): number {
    for (let i = drawnTiers.length - 1; i >= 0; i--) {
      const tier = drawnTiers[i] ?? -1;
      const box = YARD[tier]?.box;
      if (box && x >= box.x && x < box.x + box.w && y >= box.y && y < box.y + box.h) return tier;
    }
    return -1;
  }

  canvas.addEventListener('pointerdown', (ev: PointerEvent) => {
    // The main button only (a finger or pen counts as it): a right-click laid
    // eggs and opened the browser's menu over them.
    if (ev.button !== 0) return;
    // The end of an era is a cutscene; nothing in the yard answers during it.
    if (extincting) return;
    layout(pc.width, pc.height);
    const { x, y } = pc.toNative(ev.clientX, ev.clientY);

    // The falling egg is on top of everything, so it wins a contested click.
    if (falling && isOnFallingEgg(x, y)) {
      const at = { x: falling.x, y: falling.y };
      burstFragments(at.x, at.y, [GOLD, GOLD_LIGHT, WHITE], 10);
      burstSparkles(at.x, at.y, 6);
      falling = null;
      const reveal = hooks.onFallingEggCaught();
      if (reveal) revealJackpot(at.x, at.y, reveal);
      return;
    }

    // A landed meteor sits in front of the herd but behind the nest and egg
    // (see the draw order in render), so the egg keeps a click on its shell.
    const hitRock = meteorAt(x, y);
    if (hitRock && !isOnEgg(x, y)) {
      clickMeteor(hitRock, meteorHooks);
      return;
    }

    if (!isOnEgg(x, y)) {
      // The herd walks behind the nest, so the egg wins where they overlap.
      const tier = dinoAt(x, y);
      const id = DINOS[tier]?.id;
      if (id) hooks.onDinoClick(id);
      return;
    }
    const gained = hooks.onEggClick();
    squashTicksLeft = SQUASH_TICKS;
    burstEggs(x, y, clickEggCount(gained));
    burstSparkles(x, y - 10, 3);

    floatLayer.show(x, y, `+${formatShort(gained, ' ')}`);
  });

  canvas.addEventListener('pointermove', (ev: PointerEvent) => {
    const { x, y } = pc.toNative(ev.clientX, ev.clientY);
    const onMeteor = meteorAt(x, y) !== null;
    canvas.style.cursor =
      isOnEgg(x, y) || isOnFallingEgg(x, y) || onMeteor || dinoAt(x, y) >= 0
        ? 'pointer'
        : 'default';
  });

  // ---- drawing -------------------------------------------------------------

  /** Draws frame `frame` of the hatch strip so its egg window lands on the egg's spot. */
  function drawEggFrame(
    strip: HTMLImageElement,
    frame: number,
    g: CanvasRenderingContext2D = ctx,
  ): void {
    const x = eggCx - EGG_SPRITE / 2 - EGG_STRIP_INSET_X;
    const y = eggCy + EGG_BOTTOM - EGG_SPRITE_BOTTOM - EGG_STRIP_INSET_Y;
    const f = EGG_STRIP_FRAME;
    g.drawImage(strip, frame * f, 0, f, f, x, y, f, f);
  }

  /** The hatch strip's frame being shown, or -1 when the egg is whole. */
  function hatchFrame(): number {
    if (hatchTick < 0 || !sceneProp('egg-hatch')) return -1;
    // The shell opening; the last frame holds a moment.
    return Math.min(
      EGG_STRIP_FRAMES - 1,
      HATCH_FIRST_FRAME + Math.floor(hatchTick / HATCH_FRAME_TICKS),
    );
  }

  function drawEgg(g: CanvasRenderingContext2D = ctx): void {
    const [shave, bulge] = SQUASH_FRAMES[squashTicksLeft] ?? [0, 0];
    const sprite = sceneProp('egg');
    const hatch = sceneProp('egg-hatch');
    const frame = hatchFrame();
    if (hatch && frame >= 0) {
      drawEggFrame(hatch, frame, g);
      return;
    }
    if (sprite) {
      // Squash by scaling the sprite: the bottom stays put, the top comes
      // down by `shave` and each side bulges out. Four ticks of uneven
      // pixels is a fair trade for not hand-drawing squash frames yet.
      const x = eggCx - EGG_SPRITE / 2 - bulge;
      const y = eggCy + EGG_BOTTOM - EGG_SPRITE_BOTTOM;
      g.drawImage(sprite, x, y + shave, EGG_SPRITE + bulge * 2, EGG_SPRITE - shave);
      return;
    }
    const top = EGG_TOP + shave;
    const halfW = EGG_HALF_W + bulge;

    g.fillStyle = SHELL;
    for (let dy = top; dy <= EGG_BOTTOM; dy++) {
      const half = eggHalfWidthAt(dy, top, EGG_BOTTOM, halfW);
      g.fillRect(eggCx - half, eggCy + dy, half * 2, 1);
    }
    // Spots ride the squash so they don't float off the shell.
    const sy = shave / 2;
    g.fillStyle = SPOT;
    g.fillRect(eggCx - 9 - bulge, eggCy - 18 + sy, 6, 6);
    g.fillRect(eggCx + 6 + bulge, eggCy + 3, 6, 6);
    g.fillRect(eggCx - 3, eggCy + 15, 4, 4);
  }

  function drawNest(g: CanvasRenderingContext2D = ctx): void {
    const sprite = sceneProp('nest');
    if (sprite) {
      g.drawImage(sprite, eggCx - NEST_SPRITE_W / 2, nestY - NEST_SPRITE_BOTTOM);
      return;
    }
    g.fillStyle = NEST;
    for (let row = 0; row < NEST_ROWS; row++) {
      const half = Math.round(
        Math.sqrt(1 - ((row - NEST_ROWS / 2) / (NEST_ROWS / 2)) ** 2) * NEST_HALF_W,
      );
      g.fillRect(eggCx - half, nestY - NEST_ROWS + row, half * 2, 1);
    }
  }

  // ---- the golden glow ------------------------------------------------------
  // While a golden egg's bonus runs, a gold rim round the egg and nest with a
  // highlight running round it and glints about (ui/goldenGlow.ts). The rim is
  // traced from the egg and nest exactly as drawn, so it follows the squash
  // of a click and the frames of a hatch; each shape is traced once and kept.
  const glints = makeGlints();
  const rimCache = new Map<string, RimPoint[]>();
  let rimCanvas: HTMLCanvasElement | null = null;

  function currentRim(): RimPoint[] {
    const key = `${pc.width}x${pc.height}|${squashTicksLeft}|${hatchFrame()}|${sceneProp('egg') ? 1 : 0}${sceneProp('nest') ? 1 : 0}`;
    const hit = rimCache.get(key);
    if (hit) return hit;
    rimCanvas ??= document.createElement('canvas');
    rimCanvas.width = pc.width;
    rimCanvas.height = pc.height;
    const g = rimCanvas.getContext('2d', { willReadFrequently: true });
    if (!g) return [];
    g.clearRect(0, 0, pc.width, pc.height);
    drawNest(g);
    drawEgg(g);
    const rim = rimPoints(
      g.getImageData(0, 0, pc.width, pc.height).data,
      pc.width,
      pc.height,
      eggCx,
      eggCy + 12,
    );
    // A resize or a new era's art makes old shapes useless; start again.
    if (rimCache.size > 40) rimCache.clear();
    rimCache.set(key, rim);
    return rim;
  }

  /** Seconds left on the longer of the two bonus slots; 0 when neither runs. */
  function bonusSecondsLeft(now: number): number {
    const ends = Math.max(sceneState.boost?.endsAt ?? 0, sceneState.clickBoost?.endsAt ?? 0);
    return Math.max(0, (ends - now) / 1000);
  }

  /**
   * One yard dinosaur. Draws the tier's sprite with its bottom row on the
   * slot's foot row; until that PNG exists, a coloured block with an outline
   * and an eye, facing left, stands in.
   */
  function drawYardDino(slot: YardSlot, tier: number, level: number, color: string): void {
    const hatching = slot.hatching;
    // Still inside the egg until the shell falls apart.
    if (hatching && hatching.wait > 0) {
      slot.box = null;
      return;
    }
    const walking = slot.along !== slot.target || hatching !== null;
    // Flyers beat their wings whether or not they are going anywhere; a
    // walker without real frames waddles while moving and stands still otherwise.
    const bobPeriod = slot.sky ? FLAP_TICKS : WALK_BOB_TICKS;
    // A startled hop is an arc over its ticks.
    const hop =
      slot.hop > 0
        ? Math.round(
            Math.sin((Math.PI * (STARTLE_TICKS - slot.hop)) / STARTLE_TICKS) * STARTLE_PIXELS,
          )
        : 0;
    const bob =
      (slot.sky || walking
        ? (Math.floor((tickCount + slot.bobOffset) / bobPeriod) % 2) * (slot.sky ? FLAP_PIXELS : 1)
        : 0) + hop;
    const { start, room } = yardRoom(pc.width);
    // Grow with the level, but never past what fits beside the nest.
    const def = DINOS[tier];
    const full = def ? artSizeOf(def, 'full') : 64;
    const w = yardSizeFor(level, full, room);
    // The state says evolved from the moment the splice is used, but the old
    // form stands until the flash: that is the reveal.
    const gathering = evolving?.tier === tier && evolving.age < EVOLVE_GATHER_TICKS;
    const spriteId = def ? (gathering ? def.id : spriteIdFor(def, sceneState)) : '';
    const sprite = sizedDinoSprite(spriteId, full, w, pc.render);
    const targeted = def !== undefined && evolveTargets?.has(def.id) === true;
    // The evolution's flash: for a few ticks the dinosaur is its own shape in
    // white. It used to be a white rectangle over the sprite's box.
    const flashing =
      evolving?.tier === tier &&
      evolving.age >= EVOLVE_GATHER_TICKS &&
      evolving.age < EVOLVE_GATHER_TICKS + EVOLVE_FLASH_TICKS;
    slot.travel = Math.max(0, room - w);
    const trackX = start + Math.round(slot.along * slot.travel);
    // Where it stands, or on its dash from the egg to that spot: a straight
    // line from its feet in the egg to its feet on the track.
    // Cornered by a meteor, it shivers where it stands.
    const shiver = slot.cowering && !walking && !REDUCED_MOTION?.matches;
    let x = trackX + (shiver ? Math.floor(tickCount / SHIVER_TICKS) % 2 : 0);
    let feetNow = footY(slot);
    if (hatching) {
      const fromX = eggCx - Math.round(w / 2);
      const fromFeet = eggCy + EGG_BOTTOM - 1;
      hatching.dist = Math.hypot(trackX - fromX, feetNow - fromFeet);
      x = Math.round(fromX + (trackX - fromX) * hatching.t);
      feetNow = Math.round(fromFeet + (feetNow - fromFeet) * hatching.t);
      slot.facingRight = trackX > fromX;
    }
    // A swimmer dashes in whole and is cut at the waterline once it arrives.
    if (slot.water && !hatching) {
      if (sprite) drawSwimmer(slot, sprite, x, w, targeted, flashing);
      return;
    }
    // A dinosaur with real frames plays them instead of bobbing: walkers
    // while moving, flyers all the time (their strip is a wingbeat). Each
    // frame holds the static art's window, so it draws where the sprite would.
    const animated = slot.sky || walking;
    const walk = animated ? sizedWalkSheet(spriteId, full, w, pc.render) : null;
    if (walk) {
      // The phase runs with the ground covered (see tickRoam), so the feet grip.
      const tick = Math.floor((slot.phase + slot.bobOffset) / WALK_FRAME_TICKS);
      let frame = tick % walk.frames;
      if (slot.sky && !LOOPING_FLAPS.has(spriteId)) {
        const span = Math.max(1, 2 * walk.frames - 2);
        const step = tick % span;
        frame = step < walk.frames ? step : span - step;
      }
      // The frame holds the sprite's own window centred at its bottom; draw
      // it so that window lands exactly where the standing sprite would.
      // Every strip is built for the size it is drawn at: 1:1 on the native
      // grid, or `density` image pixels to a native one below the art's size.
      const f = walk.frame;
      const d = walk.density ?? 1;
      const drawn = f / d;
      const inset = (drawn - w) / 2;
      // A flyer hovers low enough that the whole frame (wings included) stays on the canvas.
      const feet = slot.sky ? Math.max(feetNow, drawn + 2) : feetNow;
      const y = feet - w + 1 - bob;
      const fx = x - inset;
      const fy = y - inset * 2;
      slot.box = { x, y, w, h: w };
      // The ring stays one native pixel wide whatever grid the art is on.
      const t = Math.round(d);
      const o = t / d;
      const ring = targeted ? outlineOf(walk.image, SPLICE, t, f) : null;
      const rf = f + 2 * t;
      const strip = flashing ? silhouetteOf(walk.image, WHITE) : walk.image;
      if (slot.facingRight) {
        ctx.save();
        ctx.translate(fx + drawn, 0);
        ctx.scale(-1, 1);
        if (ring) {
          ctx.drawImage(ring, frame * rf, 0, rf, rf, -o, fy - o, drawn + 2 * o, drawn + 2 * o);
        }
        ctx.drawImage(strip, frame * f, 0, f, f, 0, fy, drawn, drawn);
        ctx.restore();
      } else {
        if (ring) {
          ctx.drawImage(ring, frame * rf, 0, rf, rf, fx - o, fy - o, drawn + 2 * o, drawn + 2 * o);
        }
        ctx.drawImage(strip, frame * f, 0, f, f, fx, fy, drawn, drawn);
      }
      return;
    }
    if (sprite) {
      // Tiers without walk frames lift the whole sprite for a bob instead.
      // A big flyer hovers a little lower so its head stays on the canvas.
      const feet = slot.sky ? Math.max(feetNow, w + 2) : feetNow;
      const y = feet - w + 1 - bob;
      slot.box = { x, y, w, h: w };
      const { t, o } = ringFor(sprite, w);
      const ring = targeted ? outlineOf(sprite, SPLICE, t) : null;
      const art = flashing ? silhouetteOf(sprite, WHITE) : sprite;
      if (slot.facingRight) {
        ctx.save();
        ctx.translate(x + w, 0);
        ctx.scale(-1, 1);
        if (ring) ctx.drawImage(ring, -o, y - o, w + 2 * o, w + 2 * o);
        ctx.drawImage(art, 0, y, w, w);
        ctx.restore();
      } else {
        if (ring) ctx.drawImage(ring, x - o, y - o, w + 2 * o, w + 2 * o);
        ctx.drawImage(art, x, y, w, w);
      }
      return;
    }
    const size = yardSpriteSize(def?.tier ?? 1);
    const y = feetNow - size - bob;
    slot.box = { x, y, w: size, h: size };
    ctx.fillStyle = OUTLINE_COOL;
    ctx.fillRect(x - 1, y - 1, size + 2, size + 2);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, size, size);
    // Eye near the top-left, since sprites face left.
    ctx.fillStyle = WHITE;
    ctx.fillRect(x + 2, y + 2, 2, 2);
    ctx.fillStyle = OUTLINE_COOL;
    ctx.fillRect(x + 2, y + 2, 1, 1);
  }

  /**
   * An outline ring one native pixel wide for a sprite drawn `w` native
   * pixels square: `t` image pixels thick, reaching `o` native pixels out.
   * A sprite on the finer grid (sizedDinoSprite) has more image pixels than
   * native ones.
   */
  function ringFor(sprite: SpriteImage, w: number): { t: number; o: number } {
    const d = sprite.width / w;
    const t = Math.max(1, Math.round(d));
    return { t, o: t / d };
  }

  /**
   * A swimmer: the sprite above the waterline only, bobbing a pixel, with a
   * light ripple where it breaks the surface and a fleck of foam either
   * side. Its box is the part above the water, for clicks.
   */
  function drawSwimmer(
    slot: YardSlot,
    sprite: SpriteImage,
    x: number,
    w: number,
    targeted: boolean,
    flashing: boolean,
  ): void {
    // Ink bounds in native pixels, whatever grid the art was built on.
    const d = sprite.width / w;
    const raw = inkBoundsOf(sprite);
    const ink = {
      top: Math.floor(raw.top / d),
      bottom: Math.floor(raw.bottom / d),
      left: Math.floor(raw.left / d),
      right: Math.floor(raw.right / d),
    };
    const line = footY(slot);
    const cut = Math.round(ink.top + (ink.bottom - ink.top) * SWIM_SINK);
    const bob = Math.floor((tickCount + slot.bobOffset) / (WALK_BOB_TICKS * 3)) % 2;
    const y = line - cut + bob;
    slot.box = { x, y: y + ink.top, w, h: Math.max(1, line - (y + ink.top)) };
    const art = flashing ? silhouetteOf(sprite, WHITE) : sprite;
    const { t, o } = ringFor(sprite, w);
    const ring = targeted ? outlineOf(sprite, SPLICE, t) : null;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, pc.width, line);
    ctx.clip();
    if (slot.facingRight) {
      ctx.translate(x + w, 0);
      ctx.scale(-1, 1);
      if (ring) ctx.drawImage(ring, -o, y - o, w + 2 * o, w + 2 * o);
      ctx.drawImage(art, 0, y, w, w);
    } else {
      if (ring) ctx.drawImage(ring, x - o, y - o, w + 2 * o, w + 2 * o);
      ctx.drawImage(art, x, y, w, w);
    }
    ctx.restore();
    // The ripple across the body, from its actual left and right edges.
    const left = slot.facingRight ? x + (w - 1 - ink.right) : x + ink.left;
    const span = ink.right - ink.left;
    ctx.fillStyle = WATER_RIPPLE;
    ctx.fillRect(left - 2, line, span + 4, 1);
    ctx.fillStyle = WATER_FOAM;
    ctx.fillRect(left - 5, line, 3, 1);
    ctx.fillRect(left + span + 3, line, 3, 1);
    // A wake behind it, on whichever side it is swimming away from, trailing
    // off along the waterline rather than stepping down the screen.
    ctx.fillStyle = WATER_RIPPLE;
    const behind = slot.facingRight ? left - 12 : left + span + 7;
    ctx.fillRect(behind, line, 5, 1);
    ctx.fillRect(slot.facingRight ? behind - 6 : behind + 7, line, 3, 1);
  }

  /**
   * The stream: the era's water strip tiled across the bottom, holding still
   * (its shore would slide along with it), under a muddy lip whose edge
   * wavers. Drawn in the era's own ground colour, so it sits in any painting.
   */
  /**
   * The stream's top row. A taller strip (the Ice Age's shelf ice) rises
   * further up the bank; the waterline swimmers ride stays where it is.
   */
  function streamTop(): number {
    const strip = waterImage(backgroundNow().name);
    return strip ? pc.height - strip.height : waterTop;
  }

  function drawWater(): void {
    const { name } = backgroundNow();
    const strip = waterImage(name);
    const top = streamTop();
    const waves = ANIMATED_WATER[name];
    if (strip && waves) {
      const frames = Math.max(1, Math.round(strip.width / waves.frame));
      const cycle = Math.max(1, 2 * frames - 2);
      const step = Math.floor((waveSeconds * 1000) / waves.frameMs) % cycle;
      const f = step < frames ? step : cycle - step;
      const w = waves.frame;
      for (let k = 0; k * w < pc.width; k++) {
        if (k % 2 === 0) {
          ctx.drawImage(strip, f * w, 0, w, strip.height, k * w, top, w, strip.height);
        } else {
          ctx.save();
          ctx.translate((k + 1) * w, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(strip, f * w, 0, w, strip.height, 0, top, w, strip.height);
          ctx.restore();
        }
      }
    } else if (strip) {
      for (let x = 0; x < pc.width; x += strip.width) ctx.drawImage(strip, x, top);
    } else {
      ctx.fillStyle = WATER_DEEP;
      ctx.fillRect(0, top, pc.width, pc.height - top);
    }
    ctx.drawImage(bankLip(backgroundGroundColor(name) ?? GROUND), 0, top - 1);
  }

  /**
   * The bank's lip over the stream's top edge: 0-4 rows of the era's ground
   * colour, varying slowly, then a wet row and a dark edge. It never changes,
   * so it is drawn once per canvas width and ground colour and stamped each
   * frame; painting it column by column every frame (three fills a column)
   * was one of the heaviest things in the frame. Its first row sits one above
   * the stream's top, where a column with no lip puts its wet row.
   */
  let lipCache: { key: string; canvas: HTMLCanvasElement } | null = null;
  function bankLip(ground: string): HTMLCanvasElement {
    const key = `${pc.width}|${ground}`;
    if (lipCache?.key === key) return lipCache.canvas;
    const canvas = document.createElement('canvas');
    canvas.width = pc.width;
    canvas.height = 6;
    const g = canvas.getContext('2d');
    if (g) {
      for (let x = 0; x < pc.width; x++) {
        const lip = Math.max(
          0,
          Math.round(1.5 + Math.sin(x / 9) * 0.9 + Math.sin(x / 23 + 1.7) * 1.1),
        );
        if (lip > 0) {
          g.fillStyle = ground;
          g.fillRect(x, 1, 1, lip);
        }
        g.fillStyle = BANK_WET;
        g.fillRect(x, lip, 1, 1);
        g.fillStyle = BANK_EDGE;
        g.fillRect(x, lip + 1, 1, 1);
      }
    }
    lipCache = { key, canvas };
    return canvas;
  }

  /**
   * The herd, in painter's order (lower rows over higher ones). `where`
   * picks the walkers and flyers, drawn behind the nest, or the swimmers,
   * drawn in the stream in front of it; the click test reads the order back.
   */
  function drawYard(
    dinos: Readonly<Record<string, number>>,
    where: 'land' | 'water' | 'hatching',
  ): void {
    const visible: { slot: YardSlot; tier: number; level: number; color: string }[] = [];
    DINOS.forEach((def, tier) => {
      const level = dinos[def.id] ?? 0;
      const slot = YARD[tier];
      if (level <= 0 || !slot) return;
      // A dinosaur still dashing from the egg is drawn over it, whatever its layer.
      const layer = slot.hatching ? 'hatching' : slot.water ? 'water' : 'land';
      if (layer === where) visible.push({ slot, tier, level, color: def.color });
    });
    visible.sort((a, b) => footY(a.slot) - footY(b.slot));
    for (const v of visible) drawYardDino(v.slot, v.tier, v.level, v.color);
    const tiers = visible.map((v) => v.tier);
    drawnTiers = where === 'land' ? tiers : [...drawnTiers, ...tiers];
  }

  /**
   * The gold bonus egg, tumbling as it falls. The sprite is 24 x 24 with the
   * egg centred; the procedural egg below stands in until it loads.
   */
  /**
   * A twinkling glint on the shell: a small four-point star that hops to a
   * new spot on the egg every few ticks and blinks between sizes.
   */
  function drawGlint(f: FallingEgg, cx: number, cy: number): void {
    const phase = Math.floor(f.age / 4);
    const spots: ReadonlyArray<readonly [number, number]> = [
      [-4, -6],
      [3, -3],
      [-2, 2],
      [4, 4],
      [-5, 0],
    ];
    const [dx, dy] = spots[phase % spots.length] ?? [0, 0];
    const x = cx + dx;
    const y = cy + dy;
    ctx.fillStyle = WHITE;
    ctx.fillRect(x, y, 1, 1);
    if (phase % 2 === 0) {
      ctx.fillRect(x - 1, y, 1, 1);
      ctx.fillRect(x + 1, y, 1, 1);
      ctx.fillRect(x, y - 1, 1, 1);
      ctx.fillRect(x, y + 1, 1, 1);
    }
  }

  function drawFallingEgg(f: FallingEgg): void {
    const cx = Math.round(f.x);
    const cy = Math.round(f.y);
    const sprite = sceneProp('egg-gold');
    if (sprite) {
      ctx.drawImage(sprite, cx - FALLING_HALF, cy - FALLING_HALF);
      drawGlint(f, cx, cy);
      return;
    }
    const top = -11;
    const bottom = 11;
    const halfW = 10;

    const rows = (color: string, grow: number): void => {
      ctx.fillStyle = color;
      for (let dy = top - grow; dy <= bottom + grow; dy++) {
        const half = eggHalfWidthAt(dy, top - grow, bottom + grow, halfW + grow);
        ctx.fillRect(cx - half, cy + dy, half * 2, 1);
      }
    };
    rows(OUTLINE_WARM, 1);
    rows(GOLD, 0);
    // Highlight, top-left, per the style guide's light direction.
    ctx.fillStyle = GOLD_LIGHT;
    ctx.fillRect(cx - 5, cy - 7, 3, 3);
    ctx.fillRect(cx - 3, cy - 4, 2, 2);
    drawGlint(f, cx, cy);
  }

  /**
   * The painted background, bottom-aligned and centred on the egg, repeated
   * outward with every other copy mirrored so the joins are seamless. Until
   * it loads (or if it is missing) the flat sky and ground stand in.
   */
  /**
   * Which painting this era is played against. An era that names one and
   * whose file has not arrived yet falls back to the default rather than to
   * flat colour, so nothing flashes on the first frame of a new era.
   */
  function backgroundNow(): { name: BackgroundName; image: HTMLImageElement | null } {
    const wanted = eraModifier(sceneState)?.background ?? 'default';
    const image = backgroundImage(wanted);
    if (image || wanted === 'default') return { name: wanted, image };
    return { name: 'default', image: backgroundImage('default') };
  }

  function drawBackground(): void {
    const { name: bgName, image: img } = backgroundNow();
    if (!img) {
      ctx.fillStyle = SKY;
      ctx.fillRect(0, 0, pc.width, pc.height);
      ctx.fillStyle = GROUND;
      ctx.fillRect(0, groundY, pc.width, pc.height - groundY);
      return;
    }
    const tileW = img.width;
    const y = pc.height - EXTRA_GROUND - img.height;
    // Flat dirt below the painting, with a few darker specks like its own.
    ctx.fillStyle = backgroundGroundColor(bgName) ?? GROUND;
    ctx.fillRect(0, pc.height - EXTRA_GROUND, pc.width, EXTRA_GROUND);
    const dirtRand = mulberry32(0xd187 ^ pc.width);
    ctx.fillStyle = OUTLINE_COOL;
    for (let i = 0, n = Math.floor((pc.width * EXTRA_GROUND) / 700); i < n; i++) {
      ctx.fillRect(
        Math.floor(dirtRand() * pc.width),
        pc.height - EXTRA_GROUND + Math.floor(dirtRand() * EXTRA_GROUND),
        2,
        1,
      );
    }
    if (y > 0) {
      ctx.fillStyle = backgroundSkyColor(bgName) ?? SKY;
      ctx.fillRect(0, 0, pc.width, y);
      // A few seeded stars (or snow) so the extra sky matches the painting's;
      // none in a daylight sky.
      const rand = mulberry32(0x5741 ^ pc.width ^ (pc.height << 8));
      const count = SPECKLED_SKIES.has(bgName) ? Math.floor((pc.width * y) / 900) : 0;
      for (let i = 0; i < count; i++) {
        ctx.fillStyle = rand() < 0.3 ? WHITE : SHELL_SHADE;
        ctx.fillRect(Math.floor(rand() * pc.width), Math.floor(rand() * y), 1, 1);
      }
    }
    // Mirror-tiled sideways from the nest outward; each parallax layer is
    // tiled the same way with its own shift, so seams stay seamless.
    const tile = (image: HTMLImageElement, shift: number): void => {
      // Snapped to the finest position the canvas can actually draw rather
      // than to a whole native pixel: a layer creeping along at a couple of
      // pixels a second steps twice a second on the native grid, which reads
      // as a lurch. pc.step is a fraction of a native pixel (ui/pixelCanvas).
      const snapped = Math.round(shift / pc.step) * pc.step;
      const centre = eggCx - Math.floor(tileW / 2) + snapped;
      const first = Math.floor((0 - centre) / tileW);
      const last = Math.ceil((pc.width - centre) / tileW);
      for (let k = first; k <= last; k++) {
        const x = centre + k * tileW;
        if (k % 2 === 0) {
          ctx.drawImage(image, x, y);
        } else {
          ctx.save();
          ctx.translate(x + tileW, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(image, 0, y);
          ctx.restore();
        }
      }
    };
    // Parallax is parked: the yard draws background.png itself, exactly the
    // painting, which is what this looked like before any of it was split.
    // Everything the effect needs is still here and still exercised — the
    // layers, the drift rates, the sub-pixel snapping — so turning it back on
    // is this one flag. The layer PNGs rebuild from the painting with
    // scripts/split-background.mjs, which checks them as it goes.
    const layers = PARALLAX ? backgroundLayers() : null;
    if (!layers) {
      tile(img, -driftShift(BACKGROUND_SCROLL, scrollSeconds, tileW));
      return;
    }
    for (const name of BACKGROUND_LAYERS) {
      const image = layers.get(name);
      if (!image) continue;
      const drift = driftShift(DRIFT_PX_PER_SECOND[name], scrollSeconds, tileW);
      tile(image, parallax[name] + drift);
    }
  }

  /**
   * The sky going out, drawn as a thickening dither of the outline colour
   * rather than a translucent wash: an alpha layer greys the art underneath,
   * a dither keeps every pixel one of the palette's own.
   */
  function drawDarkness(fraction: number): void {
    // Floors at every other pixel rather than every pixel: the yard has to
    // still be visible when the meteor arrives, or nothing is seen to die.
    const step = Math.max(2, Math.round(8 - fraction * 6));
    ctx.fillStyle = OUTLINE_COOL;
    for (let y = 0; y < pc.height; y++) {
      // Offset by the row so it reads as a checkerboard; without this the
      // step-2 case lands on the same columns every row and stripes.
      for (let x = y % step; x < pc.width; x += step) {
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }

  /** A bone half-buried in the dirt, one per era the world has ended. */
  function drawBone(x: number, y: number, kind: number): void {
    ctx.fillStyle = BONE_SHADE;
    ctx.fillRect(x - 1, y + 1, kind === 0 ? 11 : 9, 2);
    ctx.fillStyle = BONE;
    if (kind === 0) {
      // A long bone with knuckled ends.
      ctx.fillRect(x, y, 9, 2);
      ctx.fillRect(x - 1, y - 1, 2, 4);
      ctx.fillRect(x + 8, y - 1, 2, 4);
    } else {
      // A rib curving out of the ground.
      ctx.fillRect(x, y, 2, 2);
      ctx.fillRect(x + 2, y - 1, 2, 2);
      ctx.fillRect(x + 4, y - 2, 2, 2);
      ctx.fillRect(x + 6, y - 1, 2, 2);
    }
  }

  /** The Extinction, over everything else the scene has drawn. */
  function drawExtinction(): void {
    if (!extincting) return;
    const { age } = extincting;

    if (age < EX_DARKEN_TICKS) {
      drawDarkness(age / EX_DARKEN_TICKS);
      return;
    }
    const afterDark = age - EX_DARKEN_TICKS;

    if (afterDark < EX_FALL_TICKS) {
      drawDarkness(1);
      // The fireball crosses from the upper left, growing as it comes.
      const t = afterDark / EX_FALL_TICKS;
      const x = Math.round(-40 + (eggCx + 40) * t);
      const y = Math.round(-30 + (groundY + 20) * t * t);
      // The meteors' own fireball, at an integer scale that steps up as it
      // comes: anything else would soften the pixels.
      const scale = t < 0.4 ? 2 : t < 0.75 ? 3 : 4;
      const sprite = meteorSprite('fireball');
      if (sprite) {
        const size = sprite.width * scale;
        ctx.drawImage(sprite, Math.round(x - size / 2), Math.round(y - size / 2), size, size);
      } else {
        const r = Math.round(6 + t * 18);
        ctx.fillStyle = '#feae34';
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
      return;
    }
    const afterFall = afterDark - EX_FALL_TICKS;

    if (afterFall < EX_FLASH_TICKS) {
      ctx.fillStyle = WHITE;
      ctx.fillRect(0, 0, pc.width, pc.height);
      return;
    }

    // Ash over the empty yard, and the bones that surfaced.
    const t = (afterFall - EX_FLASH_TICKS) / EX_ASH_TICKS;
    if (t < 0.35) {
      drawDarkness(1 - t / 0.35);
    }
    for (const b of bones) drawBone(b.x, b.y, b.kind);
  }

  /** Longer than this between frames and the tab was away, not slow. */
  const MAX_FRAME_SECONDS = 0.25;

  function drawScene(scene: NestScene): void {
    const now = performance.now();
    if (driftClock !== 0) {
      const delta = (now - driftClock) / 1000;
      if (delta > 0 && delta < MAX_FRAME_SECONDS) {
        if (backgroundScrolls()) scrollSeconds += delta;
        waveSeconds = waterMoves() ? waveSeconds + delta : 0;
      }
    }
    driftClock = now;
    sceneState = scene.state;
    layout(pc.width, pc.height);
    drawBackground();

    // The herd first, then the stream, the nest and egg on its bank, and
    // anything swimming in front of them.
    drawYard(scene.dinos, 'land');
    for (const m of yardMeteors) drawYardMeteor(ctx, m, pc.scale);
    drawWater();
    drawNest();
    drawEgg();
    const bonusLeft = bonusSecondsLeft(Date.now());
    if (bonusLeft > 0) {
      drawGoldenGlow(ctx, currentRim(), glints, eggCx, nestY, now / 1000, bonusLeft);
    }
    drawYard(scene.dinos, 'water');
    drawYard(scene.dinos, 'hatching');
    if (jackpot) drawJackpotRays(jackpot, now);

    for (const p of particles) {
      ctx.fillStyle = p.color;
      const px = Math.round(p.x);
      const py = Math.round(p.y);
      if (p.shape === 'egg') {
        // The same shape a pixel down and right first, in the outline colour
        // the sprites use. Without it a white egg crossing the white shell it
        // came from is invisible, which is most of the first half-second of
        // every throw.
        ctx.fillStyle = OUTLINE_COOL;
        for (let row = 0; row < EGG_PARTICLE_H; row++) {
          const [dx, w] = EGG_PARTICLE_SPANS[row]!;
          ctx.fillRect(px + dx + 1, py + row + 1, w, 1);
        }
        ctx.fillStyle = p.color;
        for (let row = 0; row < EGG_PARTICLE_H; row++) {
          const [dx, w] = EGG_PARTICLE_SPANS[row]!;
          ctx.fillRect(px + dx, py + row, w, 1);
        }
        // One shaded pixel low on the right: enough curvature to tell an egg
        // from a pill, and it costs a single rect.
        if (p.color !== SHELL_SHADE) {
          ctx.fillStyle = p.color === GOLD_LIGHT ? GOLD : SHELL_SHADE;
          ctx.fillRect(px + 3, py + 3, 1, 1);
        }
        continue;
      }
      ctx.fillRect(px, py, p.size, p.size);
    }

    if (falling) drawFallingEgg(falling);
    drawExtinction();
  }

  // ---- the jackpot reveal ---------------------------------------------------

  function revealJackpot(x: number, y: number, reveal: JackpotReveal): void {
    burstEggs(x, y, JACKPOT_EGGS, JACKPOT_EGG_SPREAD, JACKPOT_GOLD_EGGS);
    const centre = floatLayer.showJackpot(x, y, reveal.amount, reveal.caption);
    jackpot = { ...centre, start: performance.now(), age: 0 };
  }

  /**
   * The sunburst behind a jackpot's figure: rays that open out, turn slowly
   * and fade. Worked out per pixel rather than filled as paths, so every
   * pixel is in or out and the edges stay hard, as in the rest of the yard.
   */
  function drawJackpotRays(j: { x: number; y: number; start: number }, now: number): void {
    const ms = now - j.start;
    if (ms >= JACKPOT_MS) {
      jackpot = null;
      return;
    }
    const size = JACKPOT_RAY_RADIUS * 2;
    if (!rays) {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const c = canvas.getContext('2d');
      if (!c) return;
      rays = { canvas, ctx: c, image: c.createImageData(size, size) };
    }
    const open = 1 - (1 - Math.min(1, ms / JACKPOT_OPEN_MS)) ** 3;
    const radius = JACKPOT_RAY_RADIUS * open;
    const spin = REDUCED_MOTION?.matches ? 0 : (ms / 1000) * JACKPOT_SPIN;
    const gap = (Math.PI * 2) / JACKPOT_RAYS;
    const half = (gap * JACKPOT_RAY_SHARE) / 2;
    const data = rays.image.data;
    // GOLD_LIGHT, #fee761.
    for (let py = 0; py < size; py++) {
      for (let px = 0; px < size; px++) {
        const dx = px - JACKPOT_RAY_RADIUS + 0.5;
        const dy = py - JACKPOT_RAY_RADIUS + 0.5;
        let a = (Math.atan2(dy, dx) - spin) % gap;
        if (a < 0) a += gap;
        const inRay = Math.hypot(dx, dy) < radius && (a < half || a > gap - half);
        const i = (py * size + px) * 4;
        data[i] = 0xfe;
        data[i + 1] = 0xe7;
        data[i + 2] = 0x61;
        data[i + 3] = inRay ? 255 : 0;
      }
    }
    rays.ctx.putImageData(rays.image, 0, 0);
    const fade =
      ms > JACKPOT_FADE_MS ? 1 - (ms - JACKPOT_FADE_MS) / (JACKPOT_MS - JACKPOT_FADE_MS) : 1;
    ctx.globalAlpha = JACKPOT_RAY_ALPHA * fade;
    ctx.drawImage(
      rays.canvas,
      Math.round(j.x) - JACKPOT_RAY_RADIUS,
      Math.round(j.y) - JACKPOT_RAY_RADIUS,
    );
    ctx.globalAlpha = 1;
  }

  // ---- ticking -------------------------------------------------------------

  function tickFalling(f: FallingEgg): void {
    f.age++;
    f.ticksLeft--;
    if (f.ticksLeft <= 0) {
      falling = null;
      return;
    }
    const fallTicks = FALL_TICKS - ROLL_TICKS;
    if (f.age < fallTicks) {
      const landY = groundY - FALLING_LAND_ABOVE_GROUND;
      f.y = FALLING_START_Y + ((landY - FALLING_START_Y) * f.age) / fallTicks;
    } else {
      f.y = groundY - FALLING_LAND_ABOVE_GROUND;
      f.x += ROLL_SPEED * f.dir;
    }
    // Sparkles: a steady shower of glints drifting off the shell.
    if (f.age % 2 === 0) {
      for (let i = 0; i < 2; i++) {
        emit({
          x: f.x + (rand() - 0.5) * 20,
          y: f.y + (rand() - 0.5) * 20,
          vx: (rand() - 0.5) * 0.6,
          vy: -0.3 - rand() * 0.3,
          size: rand() < 0.3 ? 2 : 1,
          color: rand() < 0.6 ? GOLD_LIGHT : WHITE,
          ticksLeft: 8 + Math.floor(rand() * 8),
          gravity: 0,
        });
      }
    }
  }

  return {
    get hasMeteor() {
      return yardMeteors.length > 0;
    },

    setParallax(offsets) {
      for (const name of BACKGROUND_LAYERS) {
        const v = offsets[name];
        if (v !== undefined) parallax[name] = v;
      }
    },

    spawnMeteor(fall) {
      if (yardMeteors.length > 0 || fall.length === 0) return;
      layout(pc.width, pc.height);
      const where = {
        width: pc.width,
        groundY,
        eggCx,
        floorY: streamTop() - METEOR_BANK_CLEARANCE,
      };
      const placed: YardMeteor[] = [];
      fall.forEach((meteor, i) => {
        placed.push(createYardMeteor(meteor, where, rand, placed, i * SHOWER_STAGGER_TICKS));
      });
      // Back to front, so a nearer rock draws over a farther one and takes
      // the click when they overlap.
      placed.sort((a, b) => a.y - b.y);
      yardMeteors = placed;
      shower = { size: placed.length, broken: 0, cooled: false };
      scatterFrom(placed);
      // One rush of air for the fall, however many rocks are in it.
      hooks.onMeteorIncoming();
    },

    get hasFallingEgg() {
      return falling !== null;
    },

    get extincting() {
      return extincting !== null;
    },

    get artReady() {
      // Not before the first frame: until then sceneState is a fresh game's,
      // and a save in another era wants that era's painting, not the default.
      if (driftClock === 0) return false;
      const era = eraModifier(sceneState)?.background ?? 'default';
      return (
        backgroundImage(era) !== null &&
        waterImage(era) !== null &&
        sceneProp('nest') !== null &&
        sceneProp('egg') !== null
      );
    },

    playExtinction(onBuried: () => void): void {
      if (extincting) return;
      extincting = { age: 0, onBuried, buried: false };
    },

    setEvolveTargets(ids) {
      evolveTargets = ids && ids.size > 0 ? ids : null;
    },

    playEvolution(dinoId: string): void {
      const tier = DINOS.findIndex((d) => d.id === dinoId);
      if (tier < 0) return;
      evolving = { tier, age: 0 };
    },

    hatch(dinoId?: string): void {
      hatchTick = 0;
      const slot = YARD[DINOS.findIndex((d) => d.id === dinoId)];
      if (slot) {
        slot.hatching = { wait: HATCH_EMERGE_TICKS, t: 0, dist: 1 };
        // Its first run is out to one of the far sides of the yard, so it is
        // plainly seen leaving the egg rather than stopping beside the nest.
        const edge = HATCH_EDGE_MIN + rand() * (HATCH_EDGE_MAX - HATCH_EDGE_MIN);
        slot.along = rand() < 0.5 ? edge : 1 - edge;
        slot.target = slot.along;
        slot.rowFrom = slot.rowTo = slot.row;
      }
      burstFragments(eggCx, eggCy, [SHELL, SHELL_SHADE, WHITE], 12);
    },

    collectEggs(eggs: number): void {
      if (!(eggs > 0)) return;
      layout(pc.width, pc.height);
      // One squash, as for a click, and the eggs burst out of the top: a
      // wide spray all at once, then a short spray that tapers off.
      squashTicksLeft = SQUASH_TICKS;
      const throwEgg = (spread: number, speedMin: number, speedSpread: number): void => {
        const angle = -Math.PI / 2 + (rand() - 0.5) * spread;
        const speed = speedMin + rand() * speedSpread;
        emit({
          x: eggCx - EGG_PARTICLE_W / 2,
          y: eggCy - 16,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          size: 1,
          color: rand() < 0.3 ? SHELL_SHADE : SHELL,
          ticksLeft: 30 + Math.floor(rand() * 14),
          gravity: FRAGMENT_GRAVITY,
          shape: 'egg',
        });
      };
      for (let k = 0; k < COLLECT_BURST_EGGS; k++)
        throwEgg(Math.PI * 0.95, COLLECT_BURST_SPEED_MIN, COLLECT_BURST_SPEED_SPREAD);
      burstSparkles(eggCx, eggCy - 14, 10);
      for (let t = 1; t < COLLECT_FOUNTAIN_TICKS; t++) {
        const count = Math.max(1, Math.round(4 * (1 - t / COLLECT_FOUNTAIN_TICKS)));
        after(t, () => {
          for (let k = 0; k < count; k++) throwEgg(Math.PI * 0.6, 3, 2.5);
        });
      }
      after(COLLECT_FOUNTAIN_TICKS, () => {
        burstSparkles(eggCx, eggCy - 14, 8);
        floatLayer.show(eggCx, eggCy - 14, `+${formatShort(eggs, ' ')}`);
      });
    },

    spawnFallingEgg(): void {
      if (falling) return;
      const margin = FALLING_HALF + 8;
      const x = margin + rand() * (pc.width - margin * 2);
      falling = {
        x,
        y: FALLING_START_Y,
        ticksLeft: FALL_TICKS,
        dir: x < pc.width / 2 ? -1 : 1,
        age: 0,
      };
    },

    tick(): void {
      tickCount++;
      if (timers.length > 0) {
        const due = timers.filter((t) => t.at <= tickCount);
        timers = timers.filter((t) => t.at > tickCount);
        for (const t of due) t.run();
      }
      if (squashTicksLeft > 0) squashTicksLeft--;
      if (jackpot && jackpot.age < JACKPOT_SPARKLE_TICKS) {
        if (jackpot.age % 3 === 0) {
          burstSparkles(jackpot.x, jackpot.y, 2, JACKPOT_SPARKLE_W, JACKPOT_SPARKLE_H);
        }
        jackpot.age++;
      }
      if (hatchTick >= 0) {
        hatchTick++;
        const frames = EGG_STRIP_FRAMES - HATCH_FIRST_FRAME;
        if (hatchTick >= frames * HATCH_FRAME_TICKS + HATCH_HOLD_TICKS) {
          hatchTick = -1;
          burstSparkles(eggCx, eggCy - 10, 6);
        }
      }
      YARD.forEach((slot, tier) => tickRoam(slot, evolving?.tier === tier));
      if (evolveTargets && tickCount % 4 === 0) {
        YARD.forEach((slot, tier) => {
          const id = DINOS[tier]?.id;
          if (!id || !evolveTargets?.has(id) || !slot.box || rand() < 0.5) return;
          const b = slot.box;
          emit({
            x: b.x + rand() * b.w,
            y: b.y + rand() * b.h * 0.8,
            vx: 0,
            vy: -0.3,
            size: 1,
            color: rand() < 0.5 ? SPLICE : WHITE,
            ticksLeft: 6 + Math.floor(rand() * 5),
            gravity: 0,
          });
        });
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]!;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        if (--p.ticksLeft <= 0 || p.y > pc.height) particles.splice(i, 1);
      }

      if (evolving) {
        const slot = YARD[evolving.tier];
        const box = slot?.box;
        if (box && evolving.age < EVOLVE_GATHER_TICKS) {
          // Sparks spiral in from a ring onto the dinosaur, faster as it peaks.
          const t = evolving.age / EVOLVE_GATHER_TICKS;
          const cx = box.x + box.w / 2;
          const cy = box.y + box.h / 2;
          for (let i = 0; i < 2 + Math.floor(t * 7); i++) {
            const angle = rand() * Math.PI * 2;
            const radius = box.w * (1.6 - t) + 6;
            const px = cx + Math.cos(angle) * radius;
            const py = cy + Math.sin(angle) * radius * 0.7;
            emit({
              x: px,
              y: py,
              vx: (cx - px) / 10,
              vy: (cy - py) / 10,
              size: rand() < 0.35 ? 2 : 1,
              color: rand() < 0.5 ? SPLICE : SPLICE_DEEP,
              ticksLeft: 10,
              gravity: 0,
            });
          }
        }
        // The flash throws the gathered light back out, so the new form
        // arrives on a burst rather than under a fading wash.
        if (box && evolving.age === EVOLVE_GATHER_TICKS) {
          const cx = box.x + box.w / 2;
          const cy = box.y + box.h / 2;
          burstSparkles(cx, cy, 14);
          for (let i = 0; i < 18; i++) {
            const angle = rand() * Math.PI * 2;
            const speed = 0.8 + rand() * 1.6;
            emit({
              x: cx,
              y: cy,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed * 0.7,
              size: rand() < 0.4 ? 2 : 1,
              color: rand() < 0.5 ? SPLICE : WHITE,
              ticksLeft: 12 + Math.floor(rand() * 8),
              gravity: 0,
            });
          }
        }
        if (++evolving.age >= EVOLVE_TOTAL_TICKS) evolving = null;
      }
      if (extincting) {
        const { age } = extincting;
        const impactAt = EX_DARKEN_TICKS + EX_FALL_TICKS;
        // The swap happens inside the white, so the herd never visibly pops.
        if (!extincting.buried && age >= impactAt) {
          extincting.buried = true;
          burstFragments(eggCx, groundY + 40, [GROUND, OUTLINE_COOL, ROCK_DUST], 40);
          // One bone per era ended, spread along the dirt, up to a rowful.
          const count = Math.min(8, 1 + Math.floor(rand() * 3));
          bones = Array.from({ length: count }, () => ({
            x: Math.round(24 + rand() * (pc.width - 60)),
            y: Math.round(groundY + 30 + rand() * 38),
            kind: rand() < 0.5 ? 0 : 1,
          }));
          // The impact buries the sky's leftovers too: a rock or a gold egg
          // still about would otherwise be caught in the next era and pay
          // into it, when the whole meteor economy is meant to end here.
          falling = null;
          // Gone as surely as broken, for anyone keeping away from one.
          for (const m of yardMeteors) m.phase = 'ending';
          yardMeteors = [];
          shower = { size: 0, broken: 0, cooled: false };
          extincting.onBuried();
        }
        // Embers off the fireball on its way in.
        if (age >= EX_DARKEN_TICKS && age < impactAt) {
          const t = (age - EX_DARKEN_TICKS) / EX_FALL_TICKS;
          const fx = -40 + (eggCx + 40) * t;
          const fy = -30 + (groundY + 20) * t * t;
          for (let i = 0; i < 3; i++) {
            emit({
              x: fx + (rand() - 0.5) * 20,
              y: fy + (rand() - 0.5) * 20,
              vx: -1.2 - rand(),
              vy: -0.8 - rand(),
              size: rand() < 0.4 ? 2 : 1,
              color: METEOR_EMBER_COLORS[Math.floor(rand() * METEOR_EMBER_COLORS.length)] ?? WHITE,
              ticksLeft: 12 + Math.floor(rand() * 14),
              gravity: 0,
            });
          }
        }
        // Ash drifting down over the empty yard.
        if (age > impactAt + EX_FLASH_TICKS && age % 2 === 0) {
          emit({
            x: rand() * pc.width,
            y: -2,
            vx: (rand() - 0.5) * 0.3,
            vy: 0.5 + rand() * 0.5,
            size: 1,
            color: rand() < 0.3 ? WHITE : ASH,
            ticksLeft: 60 + Math.floor(rand() * 30),
            gravity: 0,
          });
        }
        if (++extincting.age >= EX_TOTAL_TICKS) extincting = null;
      }
      if (falling) tickFalling(falling);
      for (const m of yardMeteors) {
        if (m.phase === 'incoming' && m.delay === 0) {
          // Ember trail behind the fireball.
          const { x, y } = fireballPosition(m);
          emit({
            x: x + (rand() - 0.5) * 6,
            y: y + (rand() - 0.5) * 6,
            vx: 0.6 + rand() * 0.4,
            vy: -0.6,
            size: 2,
            color: METEOR_EMBER_COLORS[Math.floor(rand() * METEOR_EMBER_COLORS.length)] ?? WHITE,
            ticksLeft: 6 + Math.floor(rand() * 6),
            gravity: 0,
          });
        }
      }
      if (yardMeteors.length > 0) {
        yardMeteors = yardMeteors.filter((m) => tickYardMeteor(m, meteorHooks));
      }
    },

    render(scene): void {
      drawScene(scene);
    },
  };
}
